/**
 * ForeignKeyAddFormBody — name + columns + ref-table + ref-columns form for
 * `add-foreign-key` plus the per-engine extras (mssql WITH NOCHECK, postgres
 * MATCH + DEFERRABLE, oracle DEFERRABLE). ON DELETE / ON UPDATE radios surface
 * only the actions the active engine accepts (oracle hides ON UPDATE entirely
 * per DSE2's contract; mssql + mysql + oracle clip RESTRICT / SET DEFAULT per
 * engine).
 *
 * sqlite is gated upstream by `isAddForeignKeySupported`; included as a
 * defensive runtime guard.
 *
 * The basic-fields and action-radios sections live in sibling presentational
 * components so this file stays under the 200-line budget (Slice A4 split
 * pattern).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B2.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import type {
  ForeignKeyDef,
  ReferentialAction,
} from '../../../../sql-resolver/types/ddlEdit';
import {
  ambiguousColumnNameOf,
  isAmbiguousColumnNameError,
} from '../../../../sql-resolver/ddl/apply/columnEdit.alterTable';
import { parseColumnList } from '../../edit/edits/addPrimaryKey';
import {
  commitAddForeignKey,
  isDeferrableSupported as isFkDeferrableSupported,
  isMatchSupported,
  isMssqlWithNoCheckSupported,
  isOnUpdateSupported,
  isReferentialActionSupported,
  validateForeignKeyForEngine,
} from '../../edit/edits/addForeignKey';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { ambiguousColumnMessage } from './ambiguousColumnCopy';
import { ForeignKeyActionRadios } from './ForeignKeyActionRadios';
import { ForeignKeyBasicFields } from './ForeignKeyBasicFields';
import { FormEngineChip } from './FormEngineChip';

type DeferrableMode = 'unspecified' | 'deferred' | 'immediate';
type MatchMode = 'unspecified' | 'full' | 'partial' | 'simple';
type ActionMode = 'unspecified' | ReferentialAction;

export interface ForeignKeyAddFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function ForeignKeyAddFormBody(props: ForeignKeyAddFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const showMatch = isMatchSupported(engine);
  const showDeferrable = isFkDeferrableSupported(engine);
  const showWithNoCheck = isMssqlWithNoCheckSupported(engine);
  const showOnUpdate = isOnUpdateSupported(engine);

  const [columnsInput, setColumnsInput] = useState('');
  const [refTable, setRefTable] = useState('');
  const [refColumnsInput, setRefColumnsInput] = useState('');
  const [name, setName] = useState('');
  const [onDelete, setOnDelete] = useState<ActionMode>('unspecified');
  const [onUpdate, setOnUpdate] = useState<ActionMode>('unspecified');
  const [match, setMatch] = useState<MatchMode>('unspecified');
  const [deferrable, setDeferrable] = useState<DeferrableMode>('unspecified');
  const [withNoCheck, setWithNoCheck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The thrown error's own text, kept when the box shows a rewritten
   *  sentence — a developer reading the popover still gets the precise one. */
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function buildForeignKey(): ForeignKeyDef | { error: string } {
    const draft: {
      name?: string;
      columns: readonly string[];
      refTable: string;
      refColumns: readonly string[];
      onDelete?: ReferentialAction;
      onUpdate?: ReferentialAction;
      match?: 'full' | 'partial' | 'simple';
      deferrable?: 'deferred' | 'immediate';
      mssqlWithNoCheck?: boolean;
    } = {
      columns: parseColumnList(columnsInput),
      refTable: refTable.trim(),
      refColumns: parseColumnList(refColumnsInput),
    };
    const trimmedName = name.trim();
    if (trimmedName !== '') draft.name = trimmedName;
    if (onDelete !== 'unspecified') draft.onDelete = onDelete;
    if (showOnUpdate && onUpdate !== 'unspecified') draft.onUpdate = onUpdate;
    if (showMatch && match !== 'unspecified') draft.match = match;
    if (showDeferrable && deferrable !== 'unspecified') draft.deferrable = deferrable;
    if (showWithNoCheck && withNoCheck) draft.mssqlWithNoCheck = true;
    const err = validateForeignKeyForEngine(engine, draft);
    if (err !== null) return { error: err };
    return draft;
  }

  async function handleSubmit() {
    setError(null);
    setErrorDetail(null);
    const built = buildForeignKey();
    if ('error' in built) {
      setError(built.error);
      return;
    }
    setPending(true);
    try {
      await commitAddForeignKey(editContext, { foreignKey: built });
      onCommitted?.();
    } catch (err) {
      const ambiguous = ambiguousColumnNameOf(err);
      if (isAmbiguousColumnNameError(err) && ambiguous !== null) {
        setError(ambiguousColumnMessage(ambiguous, engine, 'type-delimiters'));
        setErrorDetail(err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Add foreign key</div>
        <FormEngineChip engine={engine} />
      </div>

      <ForeignKeyBasicFields
        testId={testId}
        columnsInput={columnsInput}
        setColumnsInput={setColumnsInput}
        refTable={refTable}
        setRefTable={setRefTable}
        refColumnsInput={refColumnsInput}
        setRefColumnsInput={setRefColumnsInput}
        name={name}
        setName={setName}
      />

      <ForeignKeyActionRadios
        testId={testId}
        engine={engine}
        showOnUpdate={showOnUpdate}
        showMatch={showMatch}
        showDeferrable={showDeferrable}
        showWithNoCheck={showWithNoCheck}
        onDelete={onDelete}
        onUpdate={onUpdate}
        match={match}
        deferrable={deferrable}
        withNoCheck={withNoCheck}
        setOnDelete={setOnDelete}
        setOnUpdate={setOnUpdate}
        setMatch={setMatch}
        setDeferrable={setDeferrable}
        setWithNoCheck={setWithNoCheck}
        isActionAllowed={(action) => isReferentialActionSupported(engine, action)}
      />

      {engine === 'sqlite' ? (
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-sqlite-pragma-note`}
        >
          SQLite: foreign keys require <code>PRAGMA foreign_keys = ON;</code> at session start.
        </div>
      ) : null}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
          title={errorDetail ?? undefined}
        >
          {error}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={pending} onClick={() => void handleSubmit()} data-testid={`${testId}-submit`}>
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  );
}
